// The Count tab's body, on the glass under the identity row: whatever the
// stage asks of the viewer. The shell (main.ts) owns the rows round it: the
// header, the identity row with the hologram, the action slot, the emotes
// and the tab keys. This file fills the body, and names the action.
//
// When the sheep start to run, the body becomes the game's red tally counter
// under a question line and its countdown. The counter holds its place for
// the whole stage, and the plank to lock the guess in sits in the action
// slot while the pad is up. The result, and a run's end, are timecard
// slips (slip.ts). Nothing here moves between screens: each fills the body
// from the top.

import { GUESS_MAX, type RunLine, type RunOutcome } from '../../protocol';
import { plank } from '../chrome';
import { type Counter, makeCounter } from '../counter';
import { buzz, el } from '../dom';
import { hueColor } from '../hues';
import type { Page } from '../page';
import { say } from '../say';
import { play } from '../sfx';
import { type SlipStamp, slip, slipLine, slipPair } from '../slip';
import { guessStage, type Screen, shownTips } from '../state';
import { STRINGS } from '../strings';

const LOCK_BUZZ_MS = 30;
// Under the room's llama rate (guard.ts LLAMA_RATE), with room for emotes.
const TALLY_SEND_MS = 600;

// One counter for each stage, so a rebuild of the tab keeps the guess.
let counter: Counter | null = null;
let counterStage = -1;
// The kind of counter screen last built, so the plank rises only when the pad
// first opens, not on every redraw.
let builtKind: Screen['kind'] | null = null;
// The counter the keyboard types into while the pad is up.
let typing: { page: Page; counter: Counter } | null = null;

type CounterScreen = Extract<Screen, { kind: 'counting' | 'pad' | 'locked' }>;

const isCounterScreen = (screen: Screen): screen is CounterScreen =>
  screen.kind === 'counting' || screen.kind === 'pad' || screen.kind === 'locked';

export function buildCount(page: Page, screen: Screen): HTMLElement {
  const body = el('div', `body body-${screen.kind}`);
  typing = null;
  if (isCounterScreen(screen)) {
    body.append(counterBody(page, screen));
    return body;
  }
  builtKind = screen.kind;
  switch (screen.kind) {
    case 'queue':
      body.append(queueBody(screen.place, screen.booted));
      break;
    case 'run_over':
      body.append(runOverBody(screen.outcome, screen.mine));
      break;
    case 'result':
      body.append(resultBody(page, screen.actual, screen.guess, screen.demerit));
      break;
    default:
      break;
  }
  return body;
}

// The action slot's content for a screen: the plank while the pad is up.
export function countAction(page: Page, screen: Screen): HTMLElement | null {
  if (screen.kind !== 'pad') return null;
  const stage = screen.stage;
  const tally = counterFor(page, stage, 0);
  const lock = plank(STRINGS.lockIn, () => lockIn(page, tally));
  if (builtKind !== 'pad') lock.classList.add('rise');
  return lock;
}

function queueBody(place: number | null, booted: number | null): HTMLElement {
  const box = el('div', 'queue');
  box.append(say(place === null ? STRINGS.queueNoPlace : STRINGS.queue(place)));
  if (booted !== null) box.append(say(STRINGS.booted(booted), 'note'));
  return box;
}

// The result as a timecard slip: the stage on its head, your count and the
// true count, then what the stage paid and what it docked. The stamp gives
// the verdict. On a short glass the "off by" line goes when a stamp says it.
function resultBody(page: Page, actual: number, guess: number, demerit: number): HTMLElement {
  const state = page.state();
  const right = actual === guess;
  const lines: HTMLElement[] = [];
  if (!right) {
    const off = slipLine(STRINGS.slip.offBy, String(Math.abs(actual - guess)));
    off.classList.add('slip-off');
    lines.push(off);
  }
  const stage = state.result?.stage ?? null;
  const tip = shownTips(state, Date.now()).at(-1);
  if (tip && stage !== null && tip.stage === stage) {
    lines.push(slipLine(STRINGS.slip.tip, `+${tip.share}`));
  }
  if (demerit > 0) lines.push(slipLine(STRINGS.slip.demerit, `-${demerit}`, true));
  const stamp: SlipStamp | null = right
    ? { word: STRINGS.slip.bullseye, kind: 'good' }
    : demerit > 0
      ? { word: STRINGS.slip.docked, kind: 'bad' }
      : null;
  const pair = slipPair([
    { label: STRINGS.resultYou, value: String(guess) },
    { label: STRINGS.resultActual, value: String(actual) },
  ]);
  const head = stage === null ? '' : STRINGS.slip.stage(stage);
  const box = el('div', 'result');
  box.append(slip(head, [pair, ...lines], stamp));
  return box;
}

// The end of a run: this llama's slip for the run, stamped paid when it
// earned, and the wait for the next one. Nothing on it takes a touch.
function runOverBody(outcome: RunOutcome, mine: RunLine): HTMLElement {
  const box = el('div', 'result');
  const quit = outcome === 'quit';
  if (mine.stages > 0) {
    const lines = [
      slipLine(STRINGS.runStages, String(mine.stages)),
      slipLine(STRINGS.runRight, String(mine.right)),
      slipLine(STRINGS.runTips, String(mine.earned)),
    ];
    const stamp: SlipStamp | null =
      mine.earned > 0 ? { word: STRINGS.slip.paid, kind: 'good' } : null;
    box.append(slip(STRINGS.slip.run, lines, stamp));
    if (quit) box.append(say(STRINGS.runQuit, 'note'));
  } else {
    box.append(say(quit ? STRINGS.runQuit : STRINGS.runOver));
  }
  box.append(say(STRINGS.runNext, 'note', 'run-next'));
  return box;
}

export function tipLine(share: number): HTMLElement {
  return share === 0 ? say(STRINGS.tipMissed, 'note') : say(STRINGS.tipped(share), 'good');
}

// ---- The counter ------------------------------------------------------------

function counterFor(page: Page, stage: number, start: number): Counter {
  if (counter && counterStage === stage) return counter;
  counter = makeCounter(start);
  counterStage = stage;
  counter.onChange((value) => sendTally(page, stage, value));
  return counter;
}

// The room keeps the counter as it stands, so a count that is not locked in
// by the deadline still counts, as in the game. One send each TALLY_SEND_MS
// at most, and the last value always goes.
let tallyTimer = 0;
let tallyQueued: { stage: number; value: number } | null = null;
let tallySentAt = 0;

function sendTally(page: Page, stage: number, value: number): void {
  tallyQueued = { stage, value };
  if (tallyTimer) return;
  const wait = Math.max(0, tallySentAt + TALLY_SEND_MS - Date.now());
  tallyTimer = window.setTimeout(() => {
    tallyTimer = 0;
    if (!tallyQueued || guessStage(page.state()) !== tallyQueued.stage) return;
    page.send({ t: 'tally', ...tallyQueued });
    tallyQueued = null;
    tallySentAt = Date.now();
  }, wait);
}

const stageOf = (page: Page, screen: CounterScreen): number =>
  screen.kind === 'locked' ? (page.state().locked?.stage ?? counterStage) : screen.stage;

// The ask row, then the counter. The ask row is one line whatever it holds,
// so the counter never moves: the stage while the sheep run, the question
// and the countdown once the pad opens, "Locked in" after.
function counterBody(page: Page, screen: CounterScreen): HTMLElement {
  const stage = stageOf(page, screen);
  const fresh = counterStage !== stage;
  const tally = counterFor(page, stage, screen.kind === 'locked' ? screen.value : 0);
  tally.setLocked(screen.kind === 'locked');
  typing = screen.kind === 'pad' ? { page, counter: tally } : null;
  const box = el('div', fresh ? 'guess turn-on' : 'guess');
  box.append(askRow(page, screen, stage), tally.element);
  builtKind = screen.kind;
  return box;
}

function askRow(page: Page, screen: CounterScreen, stage: number): HTMLElement {
  const row = el('div', 'ask');
  if (screen.kind === 'counting') {
    row.append(say(STRINGS.countAsk(stage), 'label'), say(STRINGS.countHint, 'note'));
    return row;
  }
  const asked = say(screen.kind === 'pad' ? STRINGS.guessAsk : STRINGS.lockedIn, 'label');
  if (screen.kind === 'locked') asked.style.color = hueColor(page.state().hue);
  row.append(asked, el('p', 'countdown'));
  return row;
}

function lockIn(page: Page, tally: Counter): void {
  const stage = guessStage(page.state());
  const value = tally.value();
  if (stage === null || value > GUESS_MAX) return;
  page.send({ t: 'guess', stage, value });
  tally.setLocked(true);
  play('ding');
  buzz(LOCK_BUZZ_MS);
}

// A keyboard types into the counter too, for a viewer on a laptop.
document.addEventListener('keydown', (event) => {
  if (!typing || event.metaKey || event.ctrlKey || event.altKey) return;
  const { page, counter: tally } = typing;
  if (/^[0-9]$/.test(event.key)) tally.type(Number(event.key));
  else if (event.key === 'Backspace') tally.backspace();
  else if (event.key === 'Escape' || event.key === 'Delete') tally.clear();
  else if (event.key === 'Enter') lockIn(page, tally);
  else return;
  event.preventDefault();
});
